import React, { useRef, useState } from "react";
import ReactModal from "react-modal";
import './edit-photo-order.css';
import { toastr } from "react-redux-toastr";
import { editPhotoOrderStyles } from "./styles";
import { useDispatch } from "react-redux";
import { loadModifyInventoryImages } from "../../../actions";
import { confirm } from "../../common/confirm/CallConfirm";

/** Modal for drag-and-drop editing of image ordering in VDP
 * 
 * @param {{
 *  isOpen: boolean,
 *  itemId: number,
 *  photoUrls: {url:string, description: string|null}[],
 *  onClose: (hasPhotosBeenUpdated: boolean) => void,
 *  modalId: string | number
 * }} props
 * @returns 
 */
export function EditPhotoOrderModal ({isOpen=false, itemId, photoUrls=[], onClose, modalId}) {
    /* Local State */
    const [imageList, setImageList] = useState([]);
    const [changedList, setChangedList] = useState([]);
    const [dragIndex, setDragIndex] = useState(null);
    const [hasChanged, setHasChanged] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* Redux */
    const dispatch = useDispatch();

    /* Refs */
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    
    /** Resets local component state to default values (values persist until parent unmounts) */
    const resetLocalState = () => {
        setImageList([]);
        setChangedList([]);
        setDragIndex(null);
        setHasChanged(false);
        setIsSubmitting(false);
    }

    /* Desktop Handlers  */
    const handleDragStart = (index) => setDragIndex(index);

    const handleDragOver = (e) => e.preventDefault(); // Needed to allow drop

    const handleDrop = (index) => {
        if (dragIndex === null || dragIndex === index) return; // No Change

        const imagesList = imageList.length ? imageList : photoUrls;
        const updatedList = [...imagesList];
        const [draggedImage] = updatedList.splice(dragIndex, 1);
        updatedList.splice(index, 0, draggedImage);
        
        // Update State
        if (!hasChanged) setHasChanged(true); // Allow Submission
        setImageList(updatedList);
        setDragIndex(null);
        if (!changedList.includes(draggedImage.url)) {
            // Highlight updated photos
            setChangedList(prevChangedList => [...prevChangedList, draggedImage.url])
        }
    };

    /* Touch Handlers */
    const handleTouchStart = (index) => dragItem.current = index;

    /**
     * @param {React.TouchEvent} e 
     * @returns {void}
     */
    const handleTouchMove = (e) => {
        try {
            e.preventDefault(); // Prevent default scroll during drag
            const touch = e.touches[0];
            const element = document.elementFromPoint(touch.clientX, touch.clientY);
            if (!element) return;
            
            const hoverIndex = parseInt(element.getAttribute('data-photo-index'));
            if (!isNaN(hoverIndex)) {
                dragOverItem.current = hoverIndex;
            }
        } catch (moveTouchError) {
            console.error("An error has occurred in the touch move event.", moveTouchError?.message || moveTouchError);
        }
    };

    const handleTouchEnd = () => {
        const fromIndex = dragItem.current;
        const toIndex = dragOverItem.current;
        const imagesList = imageList.length ? imageList : photoUrls;

        if (fromIndex !== null && toIndex !== null && fromIndex !== toIndex) {
            const updated = [...imagesList];
            const [movedItem] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, movedItem);

            setImageList(updated);
        }

        dragItem.current = null;
        dragOverItem.current = null;
    };

    /* Save Handler */
    const handleSubmit = async () => {
        setIsSubmitting(true);
        const updatedPhotos = imageList.map(imgUrl => ({url: imgUrl.url}));
        dispatch(loadModifyInventoryImages(itemId, updatedPhotos))
        .then(res => {
            if (res?.response?.wsStatus !== 'Error') {
                toastr.success(
                    "Success",
                    "Submission of the new image order was successful.",
                    { 
                        position: 'top-center',
                        timeOut: 2500,
                        onHideComplete: () => {
                            onClose(true);
                            resetLocalState();
                        }
                    }
                );
            } else {
                toastr.error(
                    res.response.wsStatus,
                    "The photo order could not be updated.",
                    { 
                        position: 'top-center',
                        timeOut: 2500,
                        onHideComplete: () => {
                            onClose(true);
                            resetLocalState();
                        }
                    }
                );
            }
        })
        .catch(updatePhotoError => {
            console.error(updatePhotoError?.message || updatePhotoError)
            toastr.error(
                'Photo edit request error',
                updatePhotoError?.message || 'An error occurred on attempt to update photo order for this vehicle, please try again later.',
                { 
                    position: 'top-center', 
                    timeout: 2000,
                    onHideComplete: () => onClose(false)
                }
            )
        })

    }

    return (
        <ReactModal 
            ariaHideApp={false}
            className={"marketplace-reusable__modal-content"}
            contentLabel="Image Order Editor"
            id={modalId || itemId}
            isOpen={isOpen}
            style={editPhotoOrderStyles.modalStyles}
            parentSelector={() => document.querySelector('#app-container')}
            preventScroll={true}
        >
            <div className={"marketplace-reusable__modal-container"}>
                {/* Header */}
                <div  className='marketplace-reusable__modal-header'>
                    <div style={{ fontSize: 24, color: '#535768' }}>
                        <span className="fa fa-images" />
                        <span style={{ marginLeft: 10 }}>Edit Image Order</span>
                    </div>
                    <button
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                        onClick={e => onClose(e)}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>

                <hr className='marketplace-reusable__modal-divider' />
            
                {/* Body */}
                <div className='marketplace-reusable__modal-body'>
                    <div className={"photo-order__container"}>
                        {!photoUrls.length ? (<span style={{textAlign: 'center'}}>Loading</span>) : null}
                        {(imageList && imageList.length ? imageList : photoUrls).map((photoUrl, index) => (
                            <div 
                                key={`${photoUrl.url}`}
                                className={`photo-order__img-wrapper${changedList.includes(photoUrl.url)? ' photo-order__img-moved' : ''}`}
                                data-photo-index={index}
                                draggable={true}
                                onDragOver={handleDragOver}
                                onDragStart={() => handleDragStart(index)}
                                onDrop={() => handleDrop(index, photoUrl.url)}
                                onTouchStart={() => handleTouchStart(index)}
                                onTouchEnd={handleTouchEnd}
                                onTouchMove={handleTouchMove}
                            >
                                <img src={photoUrl.url} alt={`${photoUrl.url}-${index}`} />
                                <div
                                    className="photo-order__img-description"
                                >
                                    {photoUrl.description || "No Description"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <hr className='marketplace-reusable__modal-divider' />
            
                {/* Footer -- Buttons */}
                <div className='marketplace-reusable__modal-footer'>
                    <div/>
                    <div className='marketplace-reusable__modal-buttons'>
                        {/* Cancel Button*/}
                        <button
                            className="btn btn-primary button-cancel"
                            style={{
                                opacity: isSubmitting ? 0.5 : 1,
                                minWidth: 100,
                                marginRight: 10,
                            }}
                            disabled={isSubmitting}
                            type="button"
                            data-dismiss="modal"
                            onClick={onClose}
                        >
                            Cancel
                        </button>

                        {/* Save Button */}
                        <button
                            className="btn btn-primary button-action"
                            disabled={(!hasChanged && !isSubmitting) || isSubmitting}
                            onClick={() => {
                                confirm("Are you sure you want to save the new photo order?")
                                .then(
                                    () => handleSubmit(),
                                    () => null
                                );
                            }}
                            style={{
                                opacity: !hasChanged ? 0.5 : 1,
                                minWidth: 100,
                            }}
                            type="button"
                        >
                            {isSubmitting ? "Submitting ..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </ReactModal>
    )
}